import { useCallback, useRef, useState } from "react";
import type { InputTargetConversation } from "@/api/conversation-targets";
import { discardPendingNativeVoiceNewChatIntent } from "@/desktop/voice-new-chat-routing";
import { subagentConversationInputTarget } from "@/lib/durable-conversation-input-target";
import { hydrateConversationFrameHistory } from "@/lib/conversation-frame-history-cache";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { useConversationSelect } from "./use-conversation-select";
import { translate } from "@/i18n";
import { hooksMessages } from "@/i18n/messages/hooks";

/** Make a recent chat or sub-agent, in any project, the input bar target.
 *  The view never moves. */
export function usePickInputTarget() {
  const mutationInFlightRef = useRef(false);
  const [pickingId, setPickingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const online = navigator.onLine;
  const selectChat = useConversationSelect({
    online,
    mutationInFlightRef,
    setMutatingId: setPickingId,
    setError,
  });
  const pick = useCallback(
    async (row: InputTargetConversation): Promise<boolean> => {
      if (row.target_kind === "chat") return selectChat(row);
      if (!online) return false;
      const target = subagentConversationInputTarget(row);
      if (!target) {
        setError(translate(hooksMessages, "subagentNotTargetable"));
        return false;
      }
      if (mutationInFlightRef.current) return false;
      setError(null);
      // A sub-agent is never selected as a chat: it is targeted directly.
      useInputBarTarget.getState().setTarget(target);
      discardPendingNativeVoiceNewChatIntent();
      void hydrateConversationFrameHistory({
        conversationId: target.conversationId,
      }).catch(() => {});
      return true;
    },
    [online, selectChat],
  );
  return { pick, pickingId, error };
}
